import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tabs,
    Tab,
    Box,
    styled,
    CircularProgress,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Tooltip,
    Alert,
    Stack,
    IconButton,
    Badge,
} from '@mui/material';
import { 
    Add as AddIcon, 
    Edit as EditIcon,
    History as HistoryIcon,
    Visibility as ViewIcon,
    WarningAmber as RiskIcon,
    Lightbulb as AssumptionIcon,
    BugReport as IssueIcon,
    AccountTree as DependencyIcon,
    Assignment as ActionIcon,
    TrendingUp as OpportunityIcon,
    ReportProblem as NcrIcon,
    VisibilityOutlined as ObservationIcon,
    CheckCircle as CompletedIcon,
    HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import RiskDialog from './RiskDialog';
import AssumptionDialog from './AssumptionDialog';
import IssueDialog from './IssueDialog';
import DependencyDialog from './DependencyDialog';
import ActionDialog from './ActionDialog';
import OpportunityDialog from './OpportunityDialog';
import NcrDialog from './NcrDialog';
import ObservationDialog from './ObservationDialog';
import HistoryDialog from './HistoryDialog';
import RaidHistoryDialog from './RaidHistoryDialog';
import { createRaidItem, getRaidItems, getReviewEntity } from '../../services/api';
import type { 
    RaidItemResponse,
    DetailResponse,
    RaidItemCreate,
    RiskDetails,
    AssumptionDetails,
    IssueDetails,
    DependencyDetails,
    RaidItem,
    SeparatedRaidItems
} from '../../services/api';
import { getUser } from '../../services/auth';
import { formatDate } from '../../utils/dateUtils';
import SystemGeneratedRaidIndicator from '../../components/SystemGeneratedRaidIndicator';

interface RaidDialogProps {
    open: boolean;
    onClose: () => void;
    questionAnswerId: number;
    onSubmit: () => void;
    raidItems: SeparatedRaidItems;
    readOnly?: boolean; // Add readOnly prop to disable editing when review is submitted
    reviewDimension?: string; // Add review dimension prop
    reviewEntityId?: number | null;
}

type RaidCategory = 'RISK' | 'ASSUMPTION' | 'ISSUE' | 'DEPENDENCY' | 'ACTION' | 'OPPORTUNITY' | 'NCR' | 'OBSERVATION';

interface RaidDialogState {
    loading: boolean;
    activeTab: number;
    raidItems: SeparatedRaidItems;
    error: string | null;
    isDialogOpen: boolean;
    isHistoryDialogOpen: boolean;
    isRaidHistoryDialogOpen: boolean;
    selectedItem: RaidItemResponse | null;
    itemIdToUpdate: number | null;
    selectedQuestionAnswerId?: number;
}

const StyledTab = styled(Tab)(({ theme }) => ({
    minHeight: 48,
    textTransform: 'none',
    '&.Mui-selected': {
        fontWeight: 'bold',
    },
    '&:not(.Mui-selected)': {
        opacity: 0.7,
    },
    '& .MuiTab-iconWrapper': {
        marginBottom: 0,
        marginRight: theme.spacing(1),
    },
}));


// Define props interface for the StatusChip component
interface StatusChipProps {
    status: string;
    label?: string;
    size?: "small" | "medium";
    icon?: React.ReactElement;
}

// Use the styled API correctly to handle the theme injection
const StatusChip = styled(Chip)<StatusChipProps>(({ theme, status }) => {
    let bgColor = theme.palette.info.light;
    let textColor = theme.palette.info.contrastText;
    
    if (status === 'Open' || status === 'Identified' || status === 'Under Evaluation' || status === 'Under Investigation') {
        bgColor = theme.palette.warning.light;
        textColor = theme.palette.warning.contrastText;
    } else if (
        status === 'Mitigated' || status === 'Validated' || status === 'Resolved' ||
        status === 'Fulfilled' || status === 'Closed' || status === 'Realized' || status === 'Approved'
    ) {
        bgColor = theme.palette.success.light;
        textColor = theme.palette.success.contrastText;
    }
    
    return {
        backgroundColor: bgColor,
        color: textColor,
        '& .MuiChip-icon': {
            color: textColor,
        },
    };
});

const RaidDialog: React.FC<RaidDialogProps> = ({ 
    open, 
    onClose, 
    questionAnswerId, 
    onSubmit,
    raidItems,
    readOnly = false, // Default to false to maintain backward compatibility
    reviewDimension, // Add review dimension parameter
    reviewEntityId = null,
}) => {
    const [state, setState] = useState<RaidDialogState>({
        loading: false,
        activeTab: 0,
        raidItems: { current: [], others: [] },
        error: null,
        isDialogOpen: false,
        isHistoryDialogOpen: false,
        isRaidHistoryDialogOpen: false,
        selectedItem: null,
        itemIdToUpdate: null,
        selectedQuestionAnswerId: undefined
    });
    const [defaultAssigneeId, setDefaultAssigneeId] = useState<string | null>(null);

    // Categories matching the backend enum
    const raidCategories: RaidCategory[] = [
        'RISK', 'ASSUMPTION', 'ISSUE', 'DEPENDENCY', 'ACTION',
        'OPPORTUNITY', 'NCR', 'OBSERVATION',
    ];

    useEffect(() => {
        const loadEntityOwner = async () => {
            if (!open || !reviewEntityId) {
                setDefaultAssigneeId(null);
                return;
            }
            try {
                const res = await getReviewEntity(reviewEntityId);
                const owner = res.data?.owner;
                setDefaultAssigneeId(owner != null ? String(owner) : null);
            } catch (error) {
                console.error('Error loading review entity owner for RAID defaults:', error);
                setDefaultAssigneeId(null);
            }
        };
        loadEntityOwner();
    }, [open, reviewEntityId]);
    // Use passed RAID items when dialog opens and reset tab when closed
    useEffect(() => {
        if (open) {
            // Use the passed raidItems prop instead of fetching
            setState(prev => ({ 
                ...prev, 
                raidItems: raidItems,
                loading: false,
                error: null
            }));
        } else {
            // Reset active tab when dialog closes for clean state next time
            setState(prev => ({ ...prev, activeTab: 0 }));
        }
    }, [open, questionAnswerId, raidItems]);

    // Set active tab to the one with maximum items when RAID items are loaded
    useEffect(() => {
        const totalItems = state.raidItems.current.length + state.raidItems.others.length;
        if (totalItems > 0) {
            const tabWithMaxItems = getTabWithMaxItems();
            setState(prev => ({ ...prev, activeTab: tabWithMaxItems }));
        } else if (open) {
            // If no items and dialog is open, default to first tab
            setState(prev => ({ ...prev, activeTab: 0 }));
        }
    }, [state.raidItems, open]);

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setState(prev => ({ ...prev, activeTab: newValue }));
    };

    const handleAddClick = () => {
        setState(prev => ({ 
            ...prev, 
            isDialogOpen: true,
            selectedItem: null,
            itemIdToUpdate: null
        }));
    };

    const handleUpdateClick = (item: RaidItemResponse) => {
        setState(prev => ({ 
            ...prev, 
            isDialogOpen: true,
            selectedItem: item,
            itemIdToUpdate: item.raid_id
        }));
    };
    
    const handleHistoryClick = (item: RaidItemResponse) => {
        setState(prev => ({ 
            ...prev, 
            isHistoryDialogOpen: true,
            selectedItem: item
        }));
    };
    
    const handleHistoryDialogClose = () => {
        setState(prev => ({ 
            ...prev, 
            isHistoryDialogOpen: false
        }));
    };
    
    const handleViewClick = (item: RaidItemResponse) => {
        setState(prev => ({ 
            ...prev, 
            isDialogOpen: true,
            selectedItem: item,
            itemIdToUpdate: item.raid_id
        }));
    };
    
    const handleRaidHistoryClick = (item: RaidItemResponse) => {
        // For a review_question_answer, we need the review_question_answer_id
        if (item.entity_type === 'review_question_answer') {
            // We'll use the entity_id as the review_question_answer_id 
            // since for this item type, entity_id is the review_question_answer.id
            const reviewQuestionAnswerId = item.entity_id;
            
            setState(prev => ({
                ...prev,
                isRaidHistoryDialogOpen: true,
                selectedItem: item,
                selectedQuestionAnswerId: reviewQuestionAnswerId
            }));
        }
    };
    
    const handleRaidHistoryDialogClose = () => {
        setState(prev => ({
            ...prev,
            isRaidHistoryDialogOpen: false
        }));
    };

    const handleDialogClose = () => {
        setState(prev => ({ ...prev, isDialogOpen: false, selectedItem: null }));
    };

    const handleSubmitRaidItem = async (data: RaidItemCreate) => {
        try {
            setState(prev => ({ ...prev, loading: true }));
            
            // Add entity_id for the question answer and review_dimension
            const submitData: RaidItemCreate = {
                ...data,
                entity_id: questionAnswerId,
                entity_type: 'review_question_answer',
                review_dimension: reviewDimension
            };
            
            await createRaidItem(submitData);
            
            // Refresh the list after successful submission via parent callback
            await onSubmit();
            
            // Close the dialog
            setState(prev => ({ 
                ...prev, 
                loading: false,
                isDialogOpen: false,
                selectedItem: null
            }));
            
            // Inform parent component
            onSubmit();
        } catch (error) {
            console.error('Error submitting RAID item:', error);
            setState(prev => ({ 
                ...prev, 
                error: 'Failed to save RAID item. Please try again.',
                loading: false
            }));
        }
    };

    // Helper function to filter raid items by category
    const getRaidItemsByCategory = (category: RaidCategory): RaidItemResponse[] => {
        // Combine current and others arrays, then filter by category
        const allItems = [...state.raidItems.current, ...state.raidItems.others];
        return allItems.filter(item => item.raid_category === category);
    };

    // Helper function to find the tab with maximum items
    const getTabWithMaxItems = (): number => {
        let maxCount = 0;
        let maxTabIndex = 0;

        raidCategories.forEach((category, index) => {
            const itemCount = getRaidItemsByCategory(category).length;
            if (itemCount > maxCount) {
                maxCount = itemCount;
                maxTabIndex = index;
            }
        });

        return maxTabIndex;
    };

    // Function to get the latest status of an item from its details using the max detail_id
    const getLatestStatus = (item: RaidItemResponse): string => {
        if (!item.details || item.details.length === 0) return 'Unknown';
        // Sort details by detail_id in descending order and get the first one
        const latestDetail = [...item.details].sort((a, b) => b.detail_id - a.detail_id)[0];
        return latestDetail.status;
    };

    // Function to get the latest details content of an item using the max detail_id
    const getLatestDetails = (item: RaidItemResponse): any => {
        if (!item.details || item.details.length === 0) return {};
        // Sort details by detail_id in descending order and get the first one
        const latestDetail = [...item.details].sort((a, b) => b.detail_id - a.detail_id)[0];
        return latestDetail.content;
    };

    // Function to render the appropriate dialog based on the active tab
    const renderRaidDialog = () => {
        if (!state.isDialogOpen) return null;
        
        const currentCategory = raidCategories[state.activeTab];
        // Find the item to update using raid_id
        const itemToUpdate = state.itemIdToUpdate ? 
            [...state.raidItems.current, ...state.raidItems.others].find(item => 
                item.raid_id === state.itemIdToUpdate
            ) : null;

        // Determine if this item is from another questionnaire (read-only)
        const isFromOtherQuestionnaire = itemToUpdate ? 
            state.raidItems.others.some(item => item.raid_id === itemToUpdate.raid_id) : false;
        
        // Set readOnly to true if the parent component is readOnly OR if the item is from another questionnaire
        const isReadOnly = readOnly || isFromOtherQuestionnaire;

        // Shared props for all RAID dialogs
        const sharedProps = {
            open: state.isDialogOpen,
            onClose: handleDialogClose,
            onSubmit: handleSubmitRaidItem,
            itemId: state.itemIdToUpdate,
            initialData: itemToUpdate ? getLatestDetails(itemToUpdate) : undefined,
            title: itemToUpdate?.title,
            readOnly: isReadOnly,
            isFromOtherQuestionnaire: isFromOtherQuestionnaire,
            defaultAssigneeId,
            initialPartialRaid: itemToUpdate?.partial_raid || false,
        };

        switch (currentCategory) {
            case 'RISK':
                return <RiskDialog {...sharedProps} />;
            case 'ASSUMPTION':
                return <AssumptionDialog {...sharedProps} />;
            case 'ISSUE':
                return <IssueDialog {...sharedProps} reviewDimension={reviewDimension} />;
            case 'DEPENDENCY':
                return <DependencyDialog {...sharedProps} />;
            case 'ACTION':
                return <ActionDialog {...sharedProps} />;
            case 'OPPORTUNITY':
                return <OpportunityDialog {...sharedProps} />;
            case 'NCR':
                return (
                    <NcrDialog
                        {...sharedProps}
                        ncrOptions={getRaidItemsByCategory('NCR')
                            .filter((i) => i.raid_id !== itemToUpdate?.raid_id)
                            .map((i) => ({ raid_id: i.raid_id, title: i.title }))}
                    />
                );
            case 'OBSERVATION':
                return <ObservationDialog {...sharedProps} />;
            default:
                return null;
        }
    };

    // Function to render the table for each RAID category with two sections
    const renderRaidItemsTable = (category: RaidCategory) => {
        const currentItems = state.raidItems.current.filter(item => item.raid_category === category);
        const otherItems = state.raidItems.others.filter(item => item.raid_category === category);
        
        if (currentItems.length === 0 && otherItems.length === 0) {
            return (
                <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="body1" color="text.secondary" align="center">
                        No {category.toLowerCase()} items found. Click the + button to add one.
                    </Typography>
                </Box>
            );
        }
        
        return (
            <Box sx={{ mt: 2, mb: 2 }}>
                {/* Current Questionnaire Section */}
                {currentItems.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                            Current Questionnaire ({currentItems.length})
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Title</TableCell>
                                        <TableCell>System Generated</TableCell>
                                        <TableCell>Created</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {currentItems.map((item) => {
                                        const status = getLatestStatus(item);
                                        const isCompleted = ['Mitigated', 'Validated', 'Resolved', 'Fulfilled', 'Closed'].includes(status);
                                        
                                        return (
                                            <TableRow key={item.raid_id}>
                                                <TableCell>
                                                    <Tooltip title={getLatestDetails(item).description || ''}>
                                                        <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                                                            {item.title}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>
                                                    <SystemGeneratedRaidIndicator isSystemGenerated={item.is_system_generated} />
                                                </TableCell>
                                                <TableCell>{formatDate(item.created_at)}</TableCell>
                                                <TableCell>
                                                    <StatusChip 
                                                        status={status}
                                                        label={status}
                                                        size="small"
                                                        icon={isCompleted ? <CompletedIcon /> : <PendingIcon />}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
                                                        {!readOnly && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleUpdateClick(item)}
                                                                color="primary"
                                                                title="Edit"
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleHistoryClick(item)}
                                                            color="secondary"
                                                            title="View Edit History"
                                                        >
                                                            <HistoryIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {/* Other Questionnaires Section */}
                {otherItems.length > 0 && (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                            Other Questionnaires ({otherItems.length})
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: 'grey.50' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Title</TableCell>
                                        <TableCell>Review Name</TableCell>
                                        <TableCell>Review Date</TableCell>
                                        <TableCell>System Generated</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Created</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {otherItems.map((item) => {
                                        const status = getLatestStatus(item);
                                        const isCompleted = ['Mitigated', 'Validated', 'Resolved', 'Fulfilled', 'Closed'].includes(status);
                                        
                                        return (
                                            <TableRow key={item.raid_id}>
                                                <TableCell>
                                                    <Tooltip title={getLatestDetails(item).description || ''}>
                                                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                            {item.title}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                                        {item.review_name || 'N/A'}
                                                    </Typography>
                                                    {item.review_status && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                                            {item.review_status}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                                        {item.review_date ? formatDate(item.review_date) : 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <SystemGeneratedRaidIndicator isSystemGenerated={item.is_system_generated} />
                                                </TableCell>
                                                <TableCell>
                                                    <StatusChip 
                                                        status={status}
                                                        label={status}
                                                        size="small"
                                                        icon={isCompleted ? <CompletedIcon /> : <PendingIcon />}
                                                    />
                                                </TableCell>
                                                <TableCell>{formatDate(item.created_at)}</TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewClick(item)}
                                                            color="primary"
                                                            title="View Details (Read-only)"
                                                        >
                                                            <ViewIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleHistoryClick(item)}
                                                            color="secondary"
                                                            title="History"
                                                        >
                                                            <HistoryIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </Box>
        );
    };

    // Function to render tab icon based on category
    const getTabIcon = (category: RaidCategory) => {
        switch (category) {
            case 'RISK':
                return <RiskIcon />;
            case 'ASSUMPTION':
                return <AssumptionIcon />;
            case 'ISSUE':
                return <IssueIcon />;
            case 'DEPENDENCY':
                return <DependencyIcon />;
            case 'ACTION':
                return <ActionIcon />;
            case 'OPPORTUNITY':
                return <OpportunityIcon />;
            case 'NCR':
                return <NcrIcon />;
            case 'OBSERVATION':
                return <ObservationIcon />;
        }
    };

    // Function to render enhanced tab label with count and color indicator
    const renderTabLabel = (category: RaidCategory) => {
        const currentItems = state.raidItems.current.filter(item => item.raid_category === category);
        const otherItems = state.raidItems.others.filter(item => item.raid_category === category);
        const currentCount = currentItems.length;
        const otherCount = otherItems.length;
        const totalCount = currentCount + otherCount;
        const hasItems = totalCount > 0;
        
        // Color logic: No items = positive (green), Has items = negative (red/orange)
        const badgeColor = hasItems ? 'error' : 'success';
        const tooltipText = hasItems 
            ? `${currentCount} current + ${otherCount} from other questionnaires = ${totalCount} total ${category.toLowerCase()}${totalCount === 1 ? '' : 's'}`
            : `No ${category.toLowerCase()}s - good status`;
        
        return (
            <Tooltip title={tooltipText} arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Badge 
                        badgeContent={totalCount} 
                        color={badgeColor}
                        showZero={false}
                        sx={{
                            '& .MuiBadge-badge': {
                                fontSize: '0.75rem',
                                minWidth: '18px',
                                height: '18px',
                            }
                        }}
                    >
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                color: hasItems ? 'error.main' : 'success.main'
                            }}
                        >
                            {getTabIcon(category)}
                        </Box>
                    </Badge>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            fontWeight: 'inherit',
                            color: hasItems ? 'error.main' : 'success.main'
                        }}
                    >
                        {category.charAt(0) + category.slice(1).toLowerCase()}
                        {hasItems && (
                            <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.7 }}>
                                ({currentCount}+{otherCount})
                            </Typography>
                        )}
                    </Typography>
                </Box>
            </Tooltip>
        );
    };

    return (
        <>
            <Dialog 
                open={open} 
                onClose={onClose} 
                maxWidth="lg" 
                fullWidth
                PaperProps={{ 
                    sx: { 
                        minHeight: '70vh',
                        maxHeight: '85vh' 
                    } 
                }}
            >
                <DialogTitle>
                    RAID Items
                    {state.loading && (
                        <CircularProgress 
                            size={20} 
                            sx={{ 
                                marginLeft: 2,
                                verticalAlign: 'middle'
                            }} 
                        />
                    )}
                </DialogTitle>
                <DialogContent>
                    {state.error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {state.error}
                        </Alert>
                    )}
                    
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tabs 
                            value={state.activeTab} 
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                '& .MuiTabs-indicator': {
                                    backgroundColor: 'text.primary',
                                },
                            }}
                        >
                            {raidCategories.map((category, index) => (
                                <StyledTab 
                                    key={category}
                                    label={renderTabLabel(category)}
                                />
                            ))}
                        </Tabs>
                    </Box>

                    {raidCategories.map((category, index) => (
                        <Box key={category} sx={{ display: state.activeTab === index ? 'block' : 'none', position: 'relative', minHeight: 200 }}>
                            {/* Add button positioned at top right */}
                            {!readOnly && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                    <IconButton
                                        color="primary"
                                        onClick={handleAddClick}
                                        size="small"
                                        sx={{ 
                                            border: '1px solid',
                                            borderColor: 'primary.main',
                                            '&:hover': {
                                                backgroundColor: 'primary.main',
                                                color: 'white'
                                            }
                                        }}
                                        title={`Add ${category}`}
                                    >
                                        <AddIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                            {renderRaidItemsTable(category)}
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>
            
            {/* Render the appropriate RAID item dialog */}
            {renderRaidDialog()}
            
            {/* Render the edit history dialog */}
            {state.selectedItem && (
                <HistoryDialog
                    open={state.isHistoryDialogOpen}
                    onClose={handleHistoryDialogClose}
                    details={state.selectedItem.details}
                    title={state.selectedItem.title}
                />
            )}
            
            {/* Render the raid history dialog */}
            {state.selectedQuestionAnswerId && (
                <RaidHistoryDialog
                    open={state.isRaidHistoryDialogOpen}
                    onClose={handleRaidHistoryDialogClose}
                    reviewQuestionAnswerId={state.selectedQuestionAnswerId}
                    title={state.selectedItem?.title || 'RAID History'}
                />
            )}
        </>
    );
};

export default RaidDialog;
